import { useMutation, useQueryClient } from 'react-query'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { BrandKitAvatar } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { CreateAvatarVariables } from './useCreateBrandKitAvatar'
import { brandKitAvatarsCacheKey } from './useBrandKitAvatars'

interface Data {
  avatars: BrandKitAvatar[]
}

export interface OnboardAvatarsVariables {
  avatars: CreateAvatarVariables[]
  posts_per_week: number
  week_days: string[]
  account_ids: number[]
}

type Options = Omit<UseMutationOptions<Data, Error, OnboardAvatarsVariables>, 'mutationFn'>

export const useOnboardAvatars = ({ brandKitId }: { brandKitId?: number }, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, Error, OnboardAvatarsVariables>(
    async (variables) => {
      if (!brandKitId) throw new Error('brandKitId is required')
      const res = await sendToApi<Data>(`/w/${workspaceId}/brand_kits/${brandKitId}/avatars/onboard`, {
        method: 'POST',
        body: variables,
      })
      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Failed to onboard avatars')
      }
      return res.data
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(brandKitAvatarsCacheKey(workspaceId, brandKitId))
        options?.onSuccess?.(data, variables, context)
      },
    },
  )
}
